/**
 * Async query helpers that need map/view context
 */

// Query factory count within buffer
export const queryFactoryCount = async (buffer, layer) => {
    if (!layer) {
      console.warn("Layer not found for factory count query");
      return 0;
    }
    
    try {
      const query = layer.createQuery();
      query.geometry = buffer;
      query.spatialRelationship = "intersects";
      
      const response = await layer.queryFeatureCount(query);
      return response;
    } catch (error) {
      console.error("Error querying factory count:", error);
      return 0;
    }
  };
  
  // Query maize production within buffer
  export const queryMaizeProduction = async (buffer, layer) => {
    if (!layer) {
      console.warn("Maize layer not found");
      return 0;
    }
    
    try {
      const query = layer.createQuery();
      query.geometry = buffer;
      query.outFields = ["Production"];
      query.returnGeometry = false;
      
      const response = await layer.queryFeatures(query);
      const maizeProduction = response.features.reduce((sum, feature) => {
        const value = parseFloat(feature.attributes.Production) || 0;
        return sum + value;
      }, 0);
      
      return maizeProduction;
    } catch (error) {
      console.error("Error querying maize production:", error);
      return 0;
    }
  };
  
  // Query oil palm production within buffer
  export const queryOilPalmProduction = async (buffer, layer) => {
    if (!layer) {
      console.warn("Oil palm cluster layer not found");
      return 0;
    }
    
    try {
      const query = layer.createQuery();
      query.geometry = buffer;
      query.outFields = ["pks_yield"];
      query.returnGeometry = false;
      
      const response = await layer.queryFeatures(query);
      const totalTones = response.features.reduce((sum, feature) => {
        const tones = feature.attributes["pks_yield"];
        const numericTones = isNaN(parseFloat(tones)) ? 0 : parseFloat(tones);
        return sum + numericTones;
      }, 0);
      
      return totalTones;
    } catch (error) {
      console.error("Error querying oil palm production:", error);
      return 0;
    }
  };
  
  // Query farm count by layer ID
  export const queryFarmCount = async (buffer, layer) => {
    if (!layer) {
      return 0;
    }
    
    try {
      const query = layer.createQuery();
      query.geometry = buffer;
      query.spatialRelationship = "intersects";
      
      return await layer.queryFeatureCount(query);
    } catch (error) {
      console.error(`Error querying farm count:`, error);
      return 0;
    }
  };
  
  // Query biochar facilities from rAssets layer
  export const queryBiocharFacilities = async (rAssetsLayer, componentContext) => {
    if (!componentContext) {
      throw new Error("Component context is required for queryBiocharFacilities");
    }
    
    componentContext.facilityLoading = true;
    
    try {
      const query = rAssetsLayer.createQuery();
      query.where = "type = 'Biochar Facility'";
      query.outFields = ["id", "name", "type", "outputRate"];
      query.returnGeometry = false;
  
      const response = await rAssetsLayer.queryFeatures(query);
      
      componentContext.biocharFacilities = response.features.map((feature) => ({
        id: feature.attributes.id,
        name: feature.attributes.name,
        output: feature.attributes.outputRate,
      }));
      
    } catch (error) {
      console.error("Error querying Biochar Facilities:", error);
      componentContext.biocharFacilities = [];
    } finally {
      componentContext.facilityLoading = false;
    }
  };
  
  // Query single point soil analysis
  export const querySinglePointSoilAnalysis = async (
    point, 
    map, 
    view, 
    retries = 3
  ) => {
    const soilLayers = [
      { id: "soilPHNigeria", key: "pH", scale: 1, field: "SoilPH" },
      { id: "sandTexture", key: "sandTexture", scale: 1, field: "Sand" },
      { id: "soilCarbon", key: "soilCarbon", scale: 1, field: "SOC" },
    ];
    
    const results = {};
  
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        for (const { id, key, scale } of soilLayers) {
          const layer = map.findLayerById(id);
          
          if (!layer) {
            console.warn(`Layer ${id} not found`);
            results[key] = "N/A";
            continue;
          }
  
          await layer.when();
          
          try {
            if (typeof layer.identify === "function") {
              const identifyResult = await layer.identify(point, {
                returnPixelValues: true,
                pixelSize: view.resolution,
              });
  
              let pixelValue = null;
              
              if (identifyResult && identifyResult.value !== undefined) {
                pixelValue = identifyResult.value;
              } else if (identifyResult?.catalogItems?.length > 0) {
                pixelValue = identifyResult.catalogItems[0].attributes["Raster.ServicePixelValue"];
              }
  
              results[key] = pixelValue !== null && !isNaN(pixelValue)
                ? parseFloat(pixelValue * scale).toFixed(2)
                : "N/A";
                
            } else {
              console.warn(`Layer ${id} does not support identify`);
              results[key] = "N/A";
            }
          } catch (layerErr) {
            console.error(`Error identifying pixel value for ${id}:`, layerErr);
            results[key] = "N/A";
          }
        }
        
        return results;
        
      } catch (err) {
        if (attempt === retries) {
          console.error(`Failed to query point after ${retries} attempts:`, err);
          soilLayers.forEach(({ key }) => (results[key] = "N/A"));
          return results;
        }
        
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
    
    return results;
  };
  
  // Execute a query on any layer and return features
  export const executeLayerQuery = async (layer, options = {}) => {
    if (!layer) {
      throw new Error("Layer is required for query");
    }
    
    try {
      const query = layer.createQuery();
      
      // Apply options
      if (options.where) query.where = options.where;
      if (options.geometry) query.geometry = options.geometry;
      if (options.spatialRelationship) query.spatialRelationship = options.spatialRelationship;
      if (options.outFields) query.outFields = options.outFields;
      if (options.returnGeometry !== undefined) query.returnGeometry = options.returnGeometry;
      if (options.outSpatialReference) query.outSpatialReference = options.outSpatialReference;
      
      return await layer.queryFeatures(query);
      
    } catch (error) {
      console.error("Error executing layer query:", error);
      throw error;
    }
  };
  
  // Get feature count from a layer with optional filter
  export const getFeatureCount = async (layer, whereClause = "1=1") => {
    if (!layer) {
      return 0;
    }
    
    try {
      const query = layer.createQuery();
      query.where = whereClause;
      
      return await layer.queryFeatureCount(query);
    } catch (error) {
      console.error("Error getting feature count:", error);
      return 0;
    }
  };